import {
  collection,
  doc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';

import firebaseInit from './firebase.index';
import { get } from 'lodash';

const app = firebaseInit();
const db = getFirestore(app);

export default class FirestoreProtectedViews {
  /**
   * Loads a protected view by user Id.
   * @param {string} userId - The userId of the protected view to load.
   * @returns {Promise<Object|null>} The data of the protected view if found, otherwise null.
   */
  static async LoadById(userId) {
    try {
      const protectedViewQuery = query(
        collection(db, 'protectedViews'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(protectedViewQuery);
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data();
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  static async CreateOrUpdate(protectedViewData) {
    try {
      const uid = get(protectedViewData, 'uid');

      const protectedViewRef = uid
        ? doc(db, 'protectedViews', uid)
        : doc(collection(db, 'protectedViews'));

      if (!uid) {
        protectedViewData.uid = protectedViewRef.id;
      }
      await setDoc(protectedViewRef, protectedViewData, { merge: true });
      return protectedViewData;
    } catch (error) {
      return null;
    }
  }

  static async LoadAllProtectedViews(userID) {
    try {
      const protectedViewQuery = query(
        collection(db, 'protectedViews'),
        where('userId', '==', userID)
      );
      const querySnapshot = await getDocs(protectedViewQuery);
      if (!querySnapshot.empty) {
        return querySnapshot.docs.map((doc) => doc.data());
      }
      return [];
    } catch (error) {
      return [];
    }
  }

  static async CreatedOrUpdateProtectedView(protectedViewData) {
    const userId = get(protectedViewData, 'userId');
    const code = get(protectedViewData, 'code');
    const page = get(protectedViewData, 'page');

    try {
      const q = query(
        collection(db, 'protectedViews'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docRef = snapshot.docs[0].ref;
        const data = snapshot.docs[0].data();

        const existingIndex = data.views.findIndex((v) => v.url === page.url);
        if (existingIndex >= 0) {
          // Update existing page
          data.views[existingIndex] = page;
        } else {
          // Add new page
          data.views.push(page);
        }

        await updateDoc(docRef, {
          views: data.views,
          code, // Optional: allow updating code
        });
      } else {
        // Create new document for this user
        const newDoc = {
          userId,
          code,
          views: [page],
          createdAt: new Date().toISOString(),
        };
        await setDoc(doc(collection(db, 'protectedViews')), newDoc);
      }
    } catch (error) {
      return null;
    }
  }

  static async GetPageProtectionStatus(userId, url) {
    const q = query(
      collection(db, 'protectedViews'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const doc = snapshot.docs[0].data();
      const view = doc.views.find((v) => url.includes(v.url));
      return {
        isLocked: view?.isLocked ?? false,
        code: doc.code,
      };
    }
    return { isLocked: false, code: null };
  }
  static async SetPageLockStatus(protectedViewData) {
    const userId = get(protectedViewData, 'userId');
    const url = get(protectedViewData, 'url');
    const isLocked = get(protectedViewData, 'isLocked');
    const code = get(protectedViewData, 'code');
    const q = query(
      collection(db, 'protectedViews'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const docRef = snapshot.docs[0].ref;
      const data = snapshot.docs[0].data();

      if (data.code !== code) {
        return false;
      }

      const views = data.views.map((v) =>
        v.url === url ? { ...v, isLocked } : v
      );

      await updateDoc(docRef, { views });
      return true;
    }
    return false;
  }
}
